import React, { useState } from 'react';
import axios from 'axios';
import { getCurrentUser, updatePassword } from '@aws-amplify/auth';
import { useAuth } from './Auth/AuthContext';
import { fetchAuthSession } from '@aws-amplify/auth';
import { Link } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Profile() {
  const { userEmail, isSubscribed, totalQuizzesTaken, lastQuizScore, signOut } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      return setError('New passwords do not match');
    }
    if (newPassword.length < 8) {
      return setError('New password must be at least 8 characters');
    }

    setIsChangingPassword(true);

    try {
      const user = await getCurrentUser();
      if (!user) {
        throw new Error('No user logged in');
      }
      await updatePassword({ oldPassword, newPassword });
      setSuccess('Password changed successfully!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Change password error:', err);
      setError(err.message || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleManageSubscription = async () => {
    setError('');
    setSuccess('');
    setIsLoadingPortal(true);

    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      const response = await axios.post(`${API_BASE_URL}/api/auth/create-portal-session`, {
        returnUrl: window.location.href
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      window.location.href = response.data.url;
    } catch (err) {
      console.error('Portal session error:', err);
      setError('Failed to open subscription management portal');
      setIsLoadingPortal(false);
    }
  };

  const firstName = userEmail ? userEmail.split('@')[0] : 'User';

  return (
    <div className="min-h-screen bg-white font-quicksand">
      {/* Header */}
      <div className="bg-gradient-to-r from-study-pink to-study-teal py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-lg">
              <span className="text-4xl">👤</span>
            </div>

            {/* User Info */}
            <div className="text-center md:text-left">
              <h1 className="text-3xl font-bold text-study-black mb-1">
                {firstName}
              </h1>
              <p className="text-study-black">{userEmail}</p>
              <div className="mt-2">
                {isSubscribed ? (
                  <span className="inline-block bg-study-yellow text-study-black text-sm font-bold px-3 py-1 rounded-full">
                    ⭐ Premium Member
                  </span>
                ) : (
                  <span className="inline-block bg-white text-study-black text-sm font-bold px-3 py-1 rounded-full">
                    Free Plan
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-xl text-red-700 text-center">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-100 border border-green-300 rounded-xl text-green-700 text-center">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Quick Stats Card */}
          <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
            <h2 className="text-xl font-bold text-study-black mb-4 flex items-center gap-2">
              <span>📊</span> Your Stats
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Quizzes Taken</span>
                <span className="text-2xl font-bold text-study-black">{totalQuizzesTaken || 0}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-600">Last Score</span>
                <span className="text-2xl font-bold text-study-black">
                  {lastQuizScore !== null && lastQuizScore !== undefined ? `${lastQuizScore}%` : '—'}
                </span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-gray-600">Account Status</span>
                <span className={`font-bold ${isSubscribed ? 'text-green-600' : 'text-gray-600'}`}>
                  {isSubscribed ? 'Premium' : 'Free'}
                </span>
              </div>
            </div>
            <Link
              to="/progress"
              className="block mt-4 text-center bg-gradient-to-r from-study-teal to-study-pink text-study-black font-bold py-3 rounded-xl hover:shadow-lg transition-all"
            >
              View Full Progress →
            </Link>
          </div>

          {/* Subscription Card */}
          <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100">
            <h2 className="text-xl font-bold text-study-black mb-4 flex items-center gap-2">
              <span>💳</span> Subscription & Billing
            </h2>

            {isSubscribed ? (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-study-yellow to-study-pink rounded-xl p-4">
                  <p className="font-bold text-study-black">Premium Plan</p>
                  <p className="text-sm text-study-black">Unlimited access to all features</p>
                </div>
                <p className="text-gray-600 text-sm">
                  Manage your subscription, update payment methods, view invoices, and more through our secure billing portal.
                </p>
                <button
                  onClick={handleManageSubscription}
                  disabled={isLoadingPortal}
                  className="w-full bg-study-teal text-study-black font-bold py-3 rounded-xl hover:bg-study-yellow transition-all disabled:opacity-50"
                >
                  {isLoadingPortal ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin">⏳</span> Opening Portal...
                    </span>
                  ) : (
                    'Manage Subscription'
                  )}
                </button>
                <p className="text-xs text-gray-500 text-center">
                  Powered by Stripe
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-gray-100 rounded-xl p-4">
                  <p className="font-bold text-study-black">Free Plan</p>
                  <p className="text-sm text-gray-600">1 quiz per day</p>
                </div>
                <p className="text-gray-600 text-sm">
                  Upgrade to Premium for unlimited quizzes, study guides, flashcards, and more!
                </p>
                <Link
                  to="/pricing"
                  className="block w-full bg-gradient-to-r from-study-teal to-study-pink text-study-black font-bold py-3 rounded-xl text-center hover:shadow-lg transition-all"
                >
                  Upgrade to Premium
                </Link>
              </div>
            )}
          </div>

          {/* Change Password Card */}
          <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100 md:col-span-2">
            <h2 className="text-xl font-bold text-study-black mb-4 flex items-center gap-2">
              <span>🔐</span> Change Password
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Old Password */}
              <div>
                <label className="block text-sm font-semibold text-study-black mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showOldPassword ? 'text' : 'password'}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    placeholder="Enter current password"
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showOldPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-semibold text-study-black mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-semibold text-study-black mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
            </div>

            {/* Password Match Indicator */}
            {confirmPassword && (
              <div className={`mt-2 text-sm ${newPassword === confirmPassword ? 'text-green-600' : 'text-red-500'}`}>
                {newPassword === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
              </div>
            )}

            <button
              onClick={handleChangePassword}
              disabled={isChangingPassword || !oldPassword || !newPassword || !confirmPassword}
              className="mt-4 bg-study-teal text-study-black font-bold py-3 px-8 rounded-xl hover:bg-study-yellow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isChangingPassword ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span> Updating...
                </span>
              ) : (
                'Update Password'
              )}
            </button>
          </div>

          {/* Account Actions Card */}
          <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100 md:col-span-2">
            <h2 className="text-xl font-bold text-study-black mb-4 flex items-center gap-2">
              <span>⚙️</span> Account
            </h2>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/"
                className="bg-gray-100 text-study-black font-semibold py-3 px-6 rounded-xl hover:bg-gray-200 transition-all"
              >
                ← Back to Home
              </Link>

              <button
                onClick={signOut}
                className="bg-red-100 text-red-700 font-semibold py-3 px-6 rounded-xl hover:bg-red-200 transition-all"
              >
                Sign Out
              </button>
            </div>

            <p className="mt-4 text-sm text-gray-500">
              Need help? Contact us at{' '}
              <a href="mailto:support@studypassrdh.com" className="text-study-teal hover:underline">
                support@studypassrdh.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
